import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import useAuthStore from '@entities/auth/model/useAuthStore';
import useRequestContextStore from '@entities/ui/model/useRequestContextStore';
import ApprovalLineTable from '@features/document-write/components/ApprovalLineTable/ApprovalLineTable';
import AttachmentSection from '@features/document-write/components/AttachmentSection/AttachmentSection';
import CKEditorWrapper from '@features/document-write/components/CKEditorWrapper/CKEditorWrapper';
import DocumentInfoForm from '@features/document-write/components/DocumentInfoForm/DocumentInfoForm';
import DocumentPreview from '@features/document-write/components/DocumentPreview/DocumentPreview';
import ReferenceDocumentListSection from '@features/document-write/components/ReferenceDocumentListSection/ReferenceDocumentListSection';
import SummaryNoteSection from '@features/document-write/components/SummaryNoteSection/SummaryNoteSection';
import useAlert from '@features/document-write/hooks/useAlert';
import useDocumentPreview from '@features/document-write/hooks/useDocumentPreview';
import useDocumentSave from '@features/document-write/hooks/useDocumentSave';
import useEditorContent from '@features/document-write/hooks/useEditorContent';
import { useFormExecutorListQuery, useFormReceiverListQuery } from '@features/document-write/hooks';
import usePageCount from '@features/document-write/hooks/usePageCount';
import { useDocFormCheckEmpQuery } from '@features/form-selection/hooks';
import { useEmployeeInfosQuery } from '@features/employee/hooks';
import calculatePageCount, {
  calculateImageSlices,
  splitEditorContentByPage,
} from '@features/document-write/utils/calculatePageCount';
import {
  captureHtmlAsDataUrl,
  DOCUMENT_CAPTURE_DEFAULTS,
} from '@shared/utils/captureDocumentImage';
import { useApprovalGuideModal } from '@app/providers/ApprovalGuideModalProvider';
import questionIcon from '@shared/assets/images/question_icon.svg';
import searchMdIcon from '@shared/assets/images/search-md_icon.svg';
import Alert from '@shared/components/atoms/Alert';
import Button from '@shared/components/atoms/Button';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import DocumentDetailModal from '@pages/approval-box/components/DocumentDetailModal/DocumentDetailModal';

import styles from './DocumentWritePage.module.scss';

/**
 * DocumentWritePage 컴포넌트
 * - 문서 작성 페이지
 * - 선택한 양식의 제목으로 문서 작성 화면
 */

// TODO 외부 라이브러리 사용 RTF 파서 라이브러리 - 스프링부트 마이그레이션 후 RTF to HTML 변환 개선 필요
export default function DocumentWritePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const setRequestContext = useRequestContextStore((state) => state.setRequestContext);
  const { open: openApprovalGuideModal } = useApprovalGuideModal(); // 모달 열기 함수 가져오기

  // 백엔드 로그 맥락: 문서 작성 화면에서의 요청은 requestAction=write 로 표시
  useEffect(
    function setDocumentWriteAction() {
      setRequestContext('document-write', 'write');
    },
    [setRequestContext],
  );

  // 선택한 양식 정보 (라우터 state에서 가져오기)
  // 수정 모드일 경우 location.state에서 모든 데이터를 가져옴
  // useMemo로 안정화: location.state가 없을 때 매 렌더 새 {}가 되면 routeState 의존 useEffect가 불필요하게 재실행됨
  const routeState = useMemo(
    function () {
      return location.state || {};
    },
    [location.state],
  );
  const isEditMode = routeState.isEditMode || false;
  const isRewriteMode = routeState.isRewriteMode || false;

  // formInfo를 상태로 관리하여 안정적으로 유지
  // 수정 모드 또는 재작성 모드일 경우 routeState.formInfo 사용
  const initialFormInfo =
    isEditMode || isRewriteMode
      ? routeState.formInfo || { formName: '문서 작성', formId: null, formContent: '' }
      : routeState.formInfo ||
        routeState || { formName: '문서 작성', formId: null, formContent: '' };
  const [formInfo, setFormInfo] = useState(initialFormInfo);

  // 회사 코드 가져오기 (Zustand store에서, 숫자로 변환)
  const companyCode = useAuthStore((state) => {
    const code = state.companyCode;
    return code ? Number(code) : null;
  });

  // 양식별 수신자/시행자 목록 조회
  const { data: defaultReceivers = [] } = useFormReceiverListQuery(formInfo.formId, companyCode);
  const {
    data: defaultExecutors = [],
    isLoading: isExecutorsLoading,
    isSuccess: isExecutorsSuccess,
  } = useFormExecutorListQuery(formInfo.formId, companyCode);

  // 양식 검토자 정보 조회 (수정 모드가 아니거나 재작성 모드일 때)
  const { data: formCheckEmpData } = useDocFormCheckEmpQuery(formInfo.formId, {
    enabled: !!formInfo.formId && (!isEditMode || isRewriteMode),
  });

  // 검토자 사원번호 목록 추출 (check1~4 중 -1이 아닌 값만)
  const checkEmpMembers = useMemo(
    function () {
      if (!formCheckEmpData) {
        return [];
      }
      const members = [];
      for (let i = 1; i <= 4; i++) {
        const checkEmp = formCheckEmpData[`check${i}`];
        if (checkEmp && checkEmp !== -1) {
          members.push({ enumber: checkEmp });
        }
      }
      return members;
    },
    [formCheckEmpData],
  );

  // 검토자 직원 정보 조회 (수정 모드가 아니거나 재작성 모드일 때)
  const checkEmpInfosResults = useEmployeeInfosQuery(checkEmpMembers, {
    enabled: checkEmpMembers.length > 0 && (!isEditMode || isRewriteMode),
  });

  // Alert 관리 (handleAlertConfirm: 확인 클릭 시 ref 콜백 실행 후 닫기, 무한 루프 방지)
  const {
    alertState,
    showAlert,
    closeAlert,
    handleAlertConfirm,
    confirmState,
    showConfirm,
    closeConfirm,
  } = useAlert();

  // 미리보기 로직
  const {
    isPreviewMode,
    printContentRef,
    handlePreview: originalHandlePreview,
    handleClosePreview,
    handlePrint,
  } = useDocumentPreview();

  // 에디터 내용 관리 (먼저 초기화)
  const { editorContent, setEditorContent, handleEditorChange } = useEditorContent();

  // 페이지 수 상태
  const [calculatedTotalPages, setCalculatedTotalPages] = useState(1);
  // 페이지별 에디터 콘텐츠
  const [editorContentByPage, setEditorContentByPage] = useState([editorContent || '']);
  // 이미지 기반 미리보기 데이터
  const [previewImageData, setPreviewImageData] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);

  // 미리보기 핸들러 (페이지 수 계산 후 미리보기 모드 전환)
  async function handlePreview() {
    setIsCapturing(true);
    try {
      // 페이지 레이아웃 정보 계산
      const layout = calculatePageCount({
        editorContent,
        documentInfo,
        summaryNote,
        attachments,
        referenceDocuments,
        useCorporateSeal,
        returnLayout: true, // 레이아웃 객체 자체를 받기 위함
      });

      // 1. 본문 이미지 캡처 — DOCUMENT_CAPTURE_DEFAULTS(공통 옵션) 사용.
      // captureHtmlToPngLayout 내부에서 normalizeHtmlForCKEditor를 실행하므로 여기서 따로 하지 않음(이중 정규화 방지).
      const captureResult = await captureHtmlAsDataUrl(editorContent || '', {
        ...DOCUMENT_CAPTURE_DEFAULTS,
      });

      if (captureResult) {
        // 2. 이미지 기반 슬라이스 계산
        const slices = calculateImageSlices(captureResult.height, layout.layout);
        setPreviewImageData({
          url: captureResult.dataUrl,
          slices: slices,
          width: captureResult.width,
        });
        setCalculatedTotalPages(slices.length);
      } else {
        // 캡처 실패 시 기존 HTML 방식 폴백
        const totalPages = calculatePageCount({
          editorContent,
          documentInfo,
          summaryNote,
          attachments,
          referenceDocuments,
          useCorporateSeal,
        });
        setCalculatedTotalPages(totalPages);

        const contentByPage = splitEditorContentByPage({
          editorContent,
          totalPages,
          documentInfo,
          useCorporateSeal,
          summaryNote,
          attachments,
          referenceDocuments,
        });
        setEditorContentByPage(contentByPage);
        setPreviewImageData(null);
      }

      // 미리보기 모드 전환
      originalHandlePreview();
    } catch (error) {
      console.error('Preview failed:', error);
      showAlert('미리보기를 준비하는 중 오류가 발생했습니다.');
    } finally {
      setIsCapturing(false);
    }
  }

  // 페이지 수 계산
  const { pageCount, editorContentRef } = usePageCount(editorContent, isPreviewMode);

  // 문서 정보 상태
  const [documentInfo, setDocumentInfo] = useState({
    draftDate: new Date().toISOString().split('T')[0], // 입안일자
    retentionPeriod: '5년', // 보존년한
    documentNumber: '', // 문서번호
    draftingDepartment: '', // 입안부서
    drafter: '', // 입안자
    recipients: routeState.recipients || [], // 수신자
    executors: routeState.executors || [], // 시행자
    subject: '', // 제목
    approvalLine: {
      // 결재선 정보 (새로운 구조)
      draft1: null, // 기안자
      approval1: null, // 결재자1
      approval2: null, // 결재자2
      approval3: null, // 결재자3
      approval4: null, // 결재자4
      review1: null, // 검토자1
      review2: null, // 검-토자2
      review3: null, // 검토자3
      review4: null, // 검토자4
      agreement1: null, // 합의자
    },
  });

  // 참조문서 상태
  const [referenceDocuments, setReferenceDocuments] = useState([]);

  // 참조문서 선택 모달에서 문서 목록 행 클릭 시 문서상세보기 모달로 열 문서 idx
  const [detailDocIdx, setDetailDocIdx] = useState(null);

  // 첨부파일 상태
  const [attachments, setAttachments] = useState([]);

  // 요약 및 비고 상태
  const [summaryNote, setSummaryNote] = useState('');

  // 인감 날인·인감증명서 사용(법인인감/사용인감)서 상태
  const [useCorporateSeal, setUseCorporateSeal] = useState(false);

  // 사용자가 결재라인을 수정했는지 추적하는 상태
  // 사용자가 결재라인을 수정하면 양식 검토자를 자동으로 추가하지 않음
  const [isApprovalLineModified, setIsApprovalLineModified] = useState(false);

  // 양식 검토자를 결재라인에 매핑한 적이 있는지 (마운트 시 1회만 매핑)
  const hasFormReviewersSyncedRef = useRef(false);

  // 초기 양식 내용 저장 (작성 중인 내용 확인용)
  // 초기값은 컴포넌트 마운트 시 한 번만 설정
  const initialFormContentRef = useRef('');
  const isInitialContentSetRef = useRef(false);
  const isDocumentInfoSetRef = useRef(false); // 문서 정보 설정 플래그 (isEditMode 전용)
  const editorSectionRef = useRef(null); // 상신 시 화면에 보이는 에디터 영역을 그대로 캡처하기 위함

  // 보안 정보 상태 (기본값)

  // 문서 저장 핸들러
  // 수정 모드일 때 docIdx와 docState 가져오기
  const docIdx = routeState.docIdx || null;
  // 임시저장 문서는 docState가 0이어야 함 (TemporaryArchivePage에서 명시적으로 0으로 전달)
  // docState가 0일 수도 있으므로 || 연산자 사용 금지 (0은 falsy이므로 null이 됨)
  const docState =
    routeState.docState !== undefined && routeState.docState !== null ? routeState.docState : null;

  const { handleTempSave, handleSubmit, isSubmitting, submitType } = useDocumentSave({
    formInfo,
    documentInfo,
    editorContent,
    referenceDocuments,
    attachments,
    summaryNote,
    useCorporateSeal,
    showAlert,
    showConfirm,
    isEditMode,
    isRewriteMode,
    docIdx,
    docState,
    editorRef: editorSectionRef,
    onSubmitSuccess: function () {
      // 상신 성공 알럿 닫힘 후: 기안함 > 상신함 탭으로 이동
      navigate('/approval/drafter?tab=submitted');
    },
    onTempSaveSuccess: function () {
      // 임시저장 성공 알럿 닫힘 후: 임시보관함 페이지로 이동 (리패치 요청)
      navigate('/approval/temporary-archive', { state: { refetch: true } });
    },
  });

  // 초기 데이터 로드 (예: 사용자 정보, 문서 번호 등)
  useEffect(
    function () {
      // 로그인한 유저 정보 설정
      if (user) {
        setDocumentInfo(function (prev) {
          return {
            ...prev,
            drafter: user.name || '',
            draftingDepartment: user.department || user.team || '',
          };
        });
      }
    },
    [user],
  );

  // 양식 본문 초기값 설정 (수정 모드가 아닐 때만)
  useEffect(
    function () {
      if (!isEditMode && formInfo.formContent && !isInitialContentSetRef.current) {
        // 초기 양식 내용 저장 (한 번만 설정)
        initialFormContentRef.current = formInfo.formContent;
        isInitialContentSetRef.current = true;
        // 즉시 설정 시도
        setEditorContent(formInfo.formContent);

        // CKEditor가 준비되지 않았을 수 있으므로 약간의 지연 후 재시도
        setTimeout(function () {
          setEditorContent(formInfo.formContent);
        }, 500);
      }
    },
    [formInfo.formContent, setEditorContent, isEditMode],
  );

  // 수정 모드일 때 location.state에서 받은 데이터로 초기값 설정
  useEffect(
    function () {
      if (isEditMode && routeState && !isDocumentInfoSetRef.current) {
        // 양식 정보 설정 (한 번만 실행되도록 ref 사용)
        if (routeState.formInfo) {
          setFormInfo(routeState.formInfo);
        }

        // 문서 정보 설정 (한 번만 실행되도록 ref 사용)
        if (routeState.documentInfo) {
          isDocumentInfoSetRef.current = true;
          setDocumentInfo(routeState.documentInfo);
        }

        // 에디터 내용 설정
        if (routeState.editorContent && !isInitialContentSetRef.current) {
          // 초기 양식 내용 저장 (한 번만 설정)
          initialFormContentRef.current = routeState.editorContent;
          isInitialContentSetRef.current = true;
          setEditorContent(routeState.editorContent);
          // CKEditor가 준비되지 않았을 수 있으므로 약간의 지연 후 재시도
          setTimeout(function () {
            setEditorContent(routeState.editorContent);
          }, 500);
        }

        // 참조문서 설정 (배열이 아닌 경우 빈 배열로 설정)
        if (routeState.referenceDocuments) {
          const referenceArray = Array.isArray(routeState.referenceDocuments)
            ? routeState.referenceDocuments
            : [];
          setReferenceDocuments(referenceArray);
        }

        // 첨부파일 설정 (배열이 아닌 경우 빈 배열로 설정)
        if (routeState.attachments) {
          const attachmentsArray = Array.isArray(routeState.attachments)
            ? routeState.attachments
            : [];
          setAttachments(attachmentsArray);
        }

        // 요약 및 비고 설정
        if (routeState.summaryNote !== undefined) {
          setSummaryNote(routeState.summaryNote);
        }

        // 법인 인감 사용 여부 설정
        if (routeState.useCorporateSeal !== undefined) {
          setUseCorporateSeal(routeState.useCorporateSeal);
        }
      }
    },
    [isEditMode, routeState, setEditorContent],
  );

  // 재작성 모드일 때 location.state에서 받은 에디터 내용만 설정
  // (결재라인, 참조자, 시행자, 참조문서, 첨부파일, 요약비고는 양식 기본값 또는 초기화)
  useEffect(
    function () {
      if (isRewriteMode && !isEditMode && routeState && !isDocumentInfoSetRef.current) {
        isDocumentInfoSetRef.current = true;

        // 양식 정보 설정
        if (routeState.formInfo) {
          setFormInfo(routeState.formInfo);
        }

        // 에디터 내용 설정 (반려된 문서의 내용 유지)
        if (routeState.editorContent && !isInitialContentSetRef.current) {
          initialFormContentRef.current = routeState.editorContent;
          isInitialContentSetRef.current = true;
          setEditorContent(routeState.editorContent);
          setTimeout(function () {
            setEditorContent(routeState.editorContent);
          }, 500);
        }

        // 나머지 항목은 초기화 상태 유지 (양식 기본값 useEffect가 처리)
        // - 참조문서: 빈 배열 (기본값)
        // - 첨부파일: 빈 배열 (기본값)
        // - 요약 및 비고: 빈 문자열 (기본값)
        // - 법인인감: false (기본값)
        // - 결재라인, 참조자, 시행자: 양식 기본값 API에서 설정
      }
    },
    [isRewriteMode, isEditMode, routeState, setEditorContent],
  );

  // 변환된 수신자/시행자 데이터 (useMemo로 메모이제이션)
  const transformedReceivers = useMemo(
    function () {
      if (!defaultReceivers || defaultReceivers.length === 0) {
        return null; // 데이터가 없으면 null 반환
      }
      return defaultReceivers.map(function (r) {
        return {
          enumber: r.enumber || 0,
          name: r.name || '',
          position: r.position || '',
          team: r.team || '',
        };
      });
    },
    [defaultReceivers],
  );

  const transformedExecutors = useMemo(
    function () {
      if (!defaultExecutors || defaultExecutors.length === 0) {
        return null; // 데이터가 없으면 null 반환
      }
      return defaultExecutors.map(function (e) {
        return {
          enumber: e.enumber || 0,
          name: e.name || '',
          position: e.position || '',
          team: e.team || '',
        };
      });
    },
    [defaultExecutors],
  );

  // 양식별 기본 수신자/시행자 설정 (수정 모드가 아니거나 재작성 모드일 때)
  useEffect(
    function () {
      // 수정 모드이면서 재작성 모드가 아니면 스킵 (기존 데이터 유지)
      if (isEditMode && !isRewriteMode) return;

      // 로딩 중이면 업데이트하지 않음
      if (isExecutorsLoading) {
        return;
      }

      setDocumentInfo(function (prev) {
        // 변환된 데이터가 있으면 사용, 없으면 기존 값 유지
        const newRecipients = transformedReceivers || prev.recipients;
        const newExecutors = transformedExecutors || prev.executors;

        // 데이터에 실제 변경이 있을 때만 상태를 업데이트하여 무한 루프 방지
        const recipientsChanged = JSON.stringify(prev.recipients) !== JSON.stringify(newRecipients);
        const executorsChanged = JSON.stringify(prev.executors) !== JSON.stringify(newExecutors);

        if (recipientsChanged || executorsChanged) {
          return {
            ...prev,
            recipients: newRecipients,
            executors: newExecutors,
          };
        }

        return prev;
      });
    },
    [transformedReceivers, transformedExecutors, isEditMode, isRewriteMode, isExecutorsLoading],
  );

  // 양식 검토자 정보를 결재라인에 매핑 (마운트 후 데이터 준비 시 1회만 실행)
  // 이후에는 결재라인 상태를 간섭하지 않음 (사용자가 검토자 제거 등 수정 가능)
  // 수정 모드가 아니거나 재작성 모드일 때 실행
  useEffect(
    function () {
      // 수정 모드이면서 재작성 모드가 아니면 스킵 (기존 결재라인 유지)
      if (isEditMode && !isRewriteMode) return;
      if (isApprovalLineModified) return;
      if (hasFormReviewersSyncedRef.current) return;
      if (!formCheckEmpData || checkEmpMembers.length === 0) return;
      if (!checkEmpInfosResults.every((r) => r.isSuccess || r.isError)) return;

      // 직원 정보 맵 생성
      const employeeInfoMap = new Map();
      checkEmpInfosResults.forEach(function (result, index) {
        const enumber = checkEmpMembers[index]?.enumber;
        if (enumber && result.data?.resultData) {
          const employeeData = result.data.resultData.employee || result.data.resultData;
          employeeInfoMap.set(enumber, employeeData);
        }
      });

      // 검토자 정보를 결재라인에 1회만 매핑
      setDocumentInfo(function (prev) {
        const newApprovalLine = { ...prev.approvalLine };
        let hasChanges = false;

        for (let i = 1; i <= 4; i++) {
          const checkEmp = formCheckEmpData[`check${i}`];
          if (checkEmp && checkEmp !== -1) {
            const employeeInfo = employeeInfoMap.get(checkEmp);
            if (employeeInfo) {
              const reviewKey = `review${i}`;
              if (
                !prev.approvalLine[reviewKey] ||
                prev.approvalLine[reviewKey].enumber !== checkEmp
              ) {
                // state는 넣지 않음. convertApprovalLine에서 전역 order 기준으로 계산함.
                // 전역 순서 2번(기안 다음 1번째)만 state=2(결재중/검토중), 나머지 → state=1(결재대기/검토대기)
                newApprovalLine[reviewKey] = {
                  enumber: checkEmp,
                  name: employeeInfo.name || '',
                  position: employeeInfo.position || '',
                  team: employeeInfo.team || '',
                };
                hasChanges = true;
              }
            }
          }
        }

        if (hasChanges) {
          hasFormReviewersSyncedRef.current = true;
          return {
            ...prev,
            approvalLine: newApprovalLine,
          };
        }

        return prev;
      });
    },
    [
      formCheckEmpData,
      checkEmpMembers,
      checkEmpInfosResults,
      isEditMode,
      isRewriteMode,
      isApprovalLineModified,
    ],
  );

  // 작성 중인 내용이 있는지 확인하는 함수
  const hasUnsavedChanges = useCallback(
    function () {
      // 에디터 내용이 초기 양식 내용과 다른지 확인
      const currentContent = editorContent || '';
      const initialContent = initialFormContentRef.current || '';

      // HTML 태그를 제거하고 텍스트만 비교
      function stripHtml(html) {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      }

      const currentText = stripHtml(currentContent).trim();
      const initialText = stripHtml(initialContent).trim();

      // 에디터 내용이 변경되었거나, 다른 필드에 내용이 있는지 확인
      if (currentText !== initialText) {
        return true;
      }

      // 문서 정보의 주요 필드 확인
      if (documentInfo.subject && documentInfo.subject.trim() !== '') {
        return true;
      }

      // 첨부파일이나 참조문서가 있는지 확인
      if (attachments.length > 0 || referenceDocuments.length > 0) {
        return true;
      }

      // 요약 및 비고가 있는지 확인
      if (summaryNote && summaryNote.trim() !== '') {
        return true;
      }

      return false;
    },
    [
      editorContent,
      documentInfo.subject,
      attachments.length,
      referenceDocuments.length,
      summaryNote,
    ],
  );

  // 페이지 새로고침/탭 닫기 시 브라우저 기본 경고 표시 (beforeunload)
  // 참고: 브라우저 뒤로가기는 SPA 내부 라우팅이므로 beforeunload가 발생하지 않음
  // 브라우저 뒤로가기 감지를 위해서는 createBrowserRouter로 마이그레이션 후 useBlocker 사용 필요
  useEffect(
    function () {
      function handleBeforeUnload(e) {
        if (hasUnsavedChanges()) {
          e.preventDefault();
          // 최신 브라우저에서는 커스텀 메시지가 표시되지 않지만, 기본 경고는 표시됨
          e.returnValue = '';
          return '';
        }
      }

      window.addEventListener('beforeunload', handleBeforeUnload);

      return function () {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    },
    [hasUnsavedChanges],
  );

  // 뒤로가기 버튼 핸들러
  function handleBackButton() {
    if (hasUnsavedChanges()) {
      if (isEditMode) {
        // 수정 모드(반려함 등에서 진입): 문서 목록(이전 페이지)으로 돌아가기
        showConfirm('작성 중인 내용이 있습니다. 문서 목록으로 돌아가시겠습니까?', function () {
          closeConfirm();
          navigate(-1);
        });
      } else {
        // 신규 작성: 양식 선택 페이지로 이동
        showConfirm('현재 작성 중인 내용이 있습니다. 양식을 다시 선택하시겠습니까?', function () {
          closeConfirm();
          navigate('/approval/new-document');
        });
      }
    } else {
      // 작성 중인 내용이 없으면 바로 이전 페이지로
      navigate(-1);
    }
  }

  return (
    <MainLayout>
      {isPreviewMode ? (
        <>
          {/* 
            DocumentPreview 컴포넌트에 ref 전달
            - forwardRef로 래핑된 컴포넌트이므로 ref prop으로 전달
            - react-to-print가 이 ref를 통해 인쇄할 DOM 요소를 찾음
            - 다른 컴포넌트에서도 동일하게 ref prop으로 전달해야 함
          */}
          <DocumentPreview
            ref={printContentRef}
            title={formInfo.formName || '문서 작성'}
            documentInfo={documentInfo}
            editorContent={editorContent}
            editorContentByPage={editorContentByPage}
            summaryNote={summaryNote}
            attachments={attachments}
            referenceDocuments={referenceDocuments}
            useCorporateSeal={useCorporateSeal}
            onClose={handleClosePreview}
            onPrint={handlePrint}
            initialTotalPages={calculatedTotalPages}
            editorImageUrl={previewImageData?.url}
            editorImageSlices={previewImageData?.slices}
          />
        </>
      ) : (
        <ContentCard className={styles.mainCard}>
          {/* 저장 중 로딩 오버레이 */}
          {isSubmitting && (
            <div className={styles.submittingOverlay}>
              <div className={styles.submittingContent}>
                <div
                  className={styles.circularProgress}
                  role="status"
                  aria-label={submitType === 'temp' ? '임시저장 중' : '상신 중'}
                />
                <div className={styles.submittingMessage}>
                  {submitType === 'temp' ? '임시저장 중입니다...' : '문서를 상신하고 있습니다...'}
                </div>
              </div>
            </div>
          )}
          <div className={styles.contentsWrapper}>
            {/* 캡처 중 로딩 오버레이 */}
            {isCapturing && (
              <div className={styles.submittingOverlay} style={{ zIndex: 10000 }}>
                <div className={styles.submittingContent}>
                  <div
                    className={styles.circularProgress}
                    role="status"
                    aria-label="미리보기 준비 중"
                  />
                  <div className={styles.submittingMessage}>미리보기를 준비하고 있습니다...</div>
                </div>
              </div>
            )}
            {/* 상단 버튼 영역 */}
            <div className={styles.topButtons}>
              <Button variant="default" className={styles.actionButton} onClick={handleBackButton}>
                뒤로가기
              </Button>
              <Button variant="default" className={styles.actionButton} onClick={handlePreview}>
                <img src={searchMdIcon} alt="" className={styles.previewIcon} />
                <span>미리보기</span>
              </Button>
            </div>

            {/* 본문 컨테이너 */}
            <div className={styles.contentContainer}>
              {/* 헤더: 품의서 + 결재 라인 안내 */}
              <div className={styles.modalHeader}>
                <div className={styles.headerTitle}>
                  <h2 className={styles.title}>{formInfo.formName || '문서 작성'}</h2>
                  <button
                    type="button"
                    className={styles.helpButton}
                    onClick={() => openApprovalGuideModal(formInfo.formName)}
                  >
                    <img src={questionIcon} alt="도움말" className={styles.helpIcon} />
                    <span className={styles.helpText}>결재 라인 안내</span>
                  </button>
                </div>
              </div>

              {/* 상단: 문서 정보 (왼쪽) + 결재 라인 (오른쪽) */}
              <div className={styles.topSection}>
                <div className={styles.documentInfoTop}>
                  <DocumentInfoForm
                    documentInfo={documentInfo}
                    onDocumentInfoChange={setDocumentInfo}
                    isPreview={false}
                    section="top"
                    onSubjectChange={function (newValue, isExceeded) {
                      setDocumentInfo(function (prev) {
                        return { ...prev, subject: newValue };
                      });
                      if (isExceeded) {
                        showAlert('제목은 최대 200자까지 허용됩니다.');
                      }
                    }}
                  />
                </div>
                <div className={styles.approvalLineWrapper}>
                  <ApprovalLineTable
                    documentInfo={documentInfo}
                    onDocumentInfoChange={setDocumentInfo}
                    isPreview={false}
                    formId={formInfo.formId}
                    isApprovalLineModified={isApprovalLineModified}
                    onApprovalLineChange={function () {
                      // 사용자가 결재라인을 수정했음을 표시
                      setIsApprovalLineModified(true);
                    }}
                  />
                </div>
              </div>

              {/* 인감 날인·인감증명서 사용(법인인감/사용인감) 체크박스 */}
              <div className={styles.corporateSealCheckbox}>
                <input
                  type="checkbox"
                  id="corporateSeal"
                  checked={useCorporateSeal}
                  onChange={(e) => setUseCorporateSeal(e.target.checked)}
                />
                <label htmlFor="corporateSeal">인감 날인·인감증명서 사용(법인인감/사용인감)</label>
              </div>

              {/* 중간: 참조자/시행자 + 제목 */}
              <div className={styles.middleSection}>
                <DocumentInfoForm
                  documentInfo={documentInfo}
                  onDocumentInfoChange={setDocumentInfo}
                  isPreview={false}
                  section="bottom"
                  onSubjectChange={function (newValue, isExceeded) {
                    setDocumentInfo(function (prev) {
                      return { ...prev, subject: newValue };
                    });
                    if (isExceeded) {
                      showAlert('제목은 최대 200자까지 허용됩니다.');
                    }
                  }}
                />
              </div>

              {/* 본문 에디터 */}
              <div ref={editorSectionRef} className={styles.editorSection}>
                <CKEditorWrapper
                  value={editorContent}
                  onChange={handleEditorChange}
                  placeholder="내용을 입력하세요..."
                  wrapperClassName={styles.editorWrapperConstraint}
                  showPasteHelpHint
                />
              </div>

              {/* 요약 및 비고 + 첨부파일 통합 컨테이너 */}
              <div className={styles.summaryAndAttachmentContainer}>
                <SummaryNoteSection
                  summaryNote={summaryNote}
                  isPreview={false}
                  onChange={setSummaryNote}
                />
                <div className={styles.divider} />
                <AttachmentSection
                  attachments={attachments}
                  onAttachmentsChange={setAttachments}
                  isPreview={false}
                  onError={(message) => showAlert(message)}
                />
              </div>

              {/* 하단: 참조문서 섹션 */}
              <div className={styles.bottomSection}>
                <div className={styles.referenceDocsWrapper}>
                  <ReferenceDocumentListSection
                    referenceDocuments={referenceDocuments}
                    onReferenceDocumentsChange={setReferenceDocuments}
                    isPreview={false}
                    onDocumentRowClick={(doc) => {
                      if (doc?.idx != null) setDetailDocIdx(doc.idx);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 하단 버튼 */}
            <div className={styles.footerButtons}>
              <div className={styles.rightButtons}>
                <Button variant="default" className={styles.actionButton} onClick={handleTempSave}>
                  임시저장
                </Button>
                <Button variant="primary" className={styles.submitButton} onClick={handleSubmit}>
                  상신
                </Button>
              </div>
            </div>
          </div>
        </ContentCard>
      )}

      <Alert
        isOpen={alertState.isOpen}
        message={alertState.message}
        onClose={closeAlert}
        onConfirm={handleAlertConfirm}
        disableOverlayClick={alertState.disableOverlayClick}
      />

      {/* Confirm Alert */}
      <Alert
        isOpen={confirmState.isOpen}
        message={confirmState.message}
        mode="confirm"
        confirmText="예"
        cancelText="아니요"
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
      />

      {/* 참조문서 선택 모달 내 문서 목록 행 클릭 시 문서상세보기 모달 (중첩, z-index로 최상단) */}
      {detailDocIdx != null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1001,
          }}
        >
          <DocumentDetailModal
            isOpen={true}
            onClose={() => setDetailDocIdx(null)}
            docIdx={detailDocIdx}
            userRole="viewer"
            tabType="reference"
            isNested
          />
        </div>
      )}
    </MainLayout>
  );
}
